import { useCallback, useEffect, useRef } from "react";

import { useDispatch } from "react-redux";

import { GAContextProviders } from "@similarweb/ga-connect";
import { useTranslate } from "@similarweb/i18n-provider";
import Sentry from "@similarweb/sentry-logger";
import { Modal } from "@similarweb/ui-components";

import { showErrorToast, showSuccessToast } from "app/actions/toast_actions";
import { useApiConfig } from "app/modules/ga-connect/useApiConfig";
import { useKeywordsCampaignsContext } from "app/pages/digital-marketing/keywords-campaigns/infra/KeywordsCampaignsContext";
import { useKeywordsMutation } from "app/pages/digital-marketing/keywords-campaigns/shared/hooks/data";
import { allTrackers } from "app/services/track/track";

import { AddKeywordsModalContent } from "./AddKeywordsModalContent";
import { TRACKING_EVENT_NAME } from "./constants";
import type { AddKeywordsModalProps } from "./types";

export const AddKeywordsModal = ({ isOpen, onClose }: AddKeywordsModalProps) => {
    const apiConfig = useApiConfig();
    const { currentCampaignConfiguration } = useKeywordsCampaignsContext();
    const { translate } = useTranslate();
    const dispatch = useDispatch();
    const trackedSearchesRef = useRef(0);

    useEffect(() => {
        if (isOpen) {
            allTrackers.dispatchTrackEvent("Modal", "view", `${TRACKING_EVENT_NAME}/Opened`);
        }
    }, [isOpen]);

    const handleCancel = useCallback(() => {
        allTrackers.dispatchTrackEvent("Button", "click", `${TRACKING_EVENT_NAME}/Cancelled`);
        onClose();
    }, [onClose]);

    // Must stay above <GAContextProviders> — useRankTrackerMutation calls
    // useQueryClient() and must bind to the app's QueryClient, not ga-connect's.
    const { mutate, isPending } = useKeywordsMutation({
        campaignId: currentCampaignConfiguration?.id ?? "",
        onSuccess: () => {
            dispatch(
                showSuccessToast(
                    translate("rankTracker.addKeywords.toast.success", { count: trackedSearchesRef.current }),
                ),
            );
            onClose();
        },
        onError: (error) => {
            Sentry.errorTracker({
                e: error,
                message: "Failed to add keywords via AddKeywordsModal",
                level: "error",
            });
            dispatch(showErrorToast(translate("rankTracker.addKeywords.toast.error")));
        },
    });

    const handleMutate = useCallback(
        (payload: Parameters<typeof mutate>[0], trackedSearches: number) => {
            trackedSearchesRef.current = trackedSearches;
            mutate(payload);
        },
        [mutate],
    );

    if (!currentCampaignConfiguration) {
        return null;
    }

    return (
        <Modal
            isOpen={isOpen}
            data-automation="add-keywords-modal"
            customStyles={{ content: { width: "800px", maxHeight: 800 } }}
            onRequestClose={handleCancel}
        >
            <GAContextProviders apiConfig={apiConfig}>
                <AddKeywordsModalContent
                    campaignConfiguration={currentCampaignConfiguration}
                    onClose={handleCancel}
                    mutate={handleMutate}
                    isPending={isPending}
                />
            </GAContextProviders>
        </Modal>
    );
};
